import { IconAttention, ServiceCitiesIcon } from 'assets/icons';
import { AxiosError } from 'axios';
import { AlertBannerBlock } from 'components/redesign/AlertBannerBlock';
import { CitiesSelect } from 'components/redesign/CititesSelect';
import { useAppSelector } from 'hooks/useAppSelector';
import { ValidationError } from 'pages/ModerationRequest/ExecutorModeration/steps/finalStep/types';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { MultiValue } from 'react-select';
import { toast } from 'react-toastify';
import { getProfile } from 'reduxStore/reducers/ProfileSlice/selectors/getProfile';
import { ProfileService } from 'services/ProfileService';
import { UpdateUserCommonSettlementsCommand } from 'services/ProfileService/types';
import { ExtendedSuggestion, SettlementShort } from 'types';
import { Input } from 'ui/redesign/Input';
import { PageWrapper } from 'ui/redesign/PageWrapper';
import { OptionType } from 'ui/redesign/Select/types';
import { ProfileWithSettlements } from './types';
import { getHasPrimaryCity } from 'reduxStore/reducers/ProfileSlice/selectors/getHasPrimaryCity';

const primarySettlementRequireMessage = 'Необходимо выбрать один город/населенный пункт';

export const PrivateData: FC = () => {
  const userProfile = useAppSelector(getProfile);
  const {
    control,
    setValue,
    reset,
    getValues,
    setError,
    formState: { errors },
    clearErrors,
  } = useForm<ProfileWithSettlements>({
    defaultValues: {
      ...userProfile,
      primarySettlement: null,
      additionalSettlements: [],
    },
  });

  const queryClient = useQueryClient();
  const hasPrimaryCity = useAppSelector(getHasPrimaryCity);

  const { refetch } = useQuery(
    ['getUserCommonSettlements'],
    () => ProfileService.getUserCommonSettlementsV2(),
    {
      onSuccess: (data) => {
        data.data.primaryCommonSettlement &&
          setValue('primarySettlement', {
            label: data.data.primaryCommonSettlement.objectFullName,
            value: data.data.primaryCommonSettlement,
          });

        if (
          data.data.additionalCommonSettlements &&
          data.data.additionalCommonSettlements.length > 0
        ) {
          setValue(
            'additionalSettlements',
            data.data.additionalCommonSettlements.map((item) => ({
              label: item.objectFullName,
              value: item,
            }))
          );
        }
      },
    }
  );

  const { mutateAsync: updateUserCommonSettlementsV2 } = useMutation(
    ['updateUserCommonSettlementsV2'],
    (commonSettlements: UpdateUserCommonSettlementsCommand) =>
      ProfileService.updateUserCommonSettlementsV2(commonSettlements),
    {
      onSuccess: () => {
        queryClient.refetchQueries(['getBanners']);
      },
      onError: (data: AxiosError<ValidationError>) => {
        for (const error in data?.response?.data?.errors) {
          data?.response?.data?.errors[error].map((text: string) => toast.error(text));
          toast.error(data?.response?.data?.errors);
        }
      },
    }
  );

  const handleSetErrorPrimarySettlement = () => {
    setError('primarySettlement', {
      type: 'manual',
      message: primarySettlementRequireMessage,
    });
  };

  useEffect(() => {
    refetch();
    if (userProfile) {
      reset(userProfile);
    }
  }, [userProfile, refetch, reset]);

  const onChangeHandler = async (
    value:
      | MultiValue<OptionType<SettlementShort | ExtendedSuggestion>>
      | OptionType<SettlementShort>
      | null
  ) => {
    const actualValues = getValues();

    if (!actualValues.primarySettlement) {
      handleSetErrorPrimarySettlement();
      return;
    }

    if (Array.isArray(value)) {
      if (
        actualValues.additionalSettlements.some(
          (item) =>
            actualValues?.primarySettlement &&
            item.value.fiasId === actualValues?.primarySettlement?.value?.fiasId
        )
      ) {
        setValue('primarySettlement', undefined);
        handleSetErrorPrimarySettlement();
      } else {
        const requestBody: UpdateUserCommonSettlementsCommand = {
          primaryCommonSettlement: actualValues?.primarySettlement
            ? actualValues?.primarySettlement.value
            : undefined,
          additionalCommonSettlements: actualValues.additionalSettlements.map(
            (optionValue) => optionValue.value as SettlementShort
          ),
        };
        try {
          requestBody?.primaryCommonSettlement &&
            (await updateUserCommonSettlementsV2(requestBody));
        } catch (error) {
          return;
        }
      }
    } else {
      const filteredAdditionalSettlements = actualValues.additionalSettlements.filter(
        (item) =>
          actualValues?.primarySettlement &&
          item.value.fiasId !== actualValues?.primarySettlement.value.fiasId
      );
      actualValues.additionalSettlements.some(
        (item) =>
          actualValues?.primarySettlement &&
          item.value.fiasId === actualValues?.primarySettlement.value.fiasId
      );
      setValue('additionalSettlements', filteredAdditionalSettlements);

      const requestBody: UpdateUserCommonSettlementsCommand = {
        primaryCommonSettlement:
          actualValues?.primarySettlement && actualValues?.primarySettlement.value,
        additionalCommonSettlements: filteredAdditionalSettlements.map(
          (optionValue) => optionValue.value as SettlementShort
        ),
      };
      try {
        requestBody?.primaryCommonSettlement && (await updateUserCommonSettlementsV2(requestBody));
      } catch (error) {
        return;
      }
    }
  };

  return (
    <PageWrapper title="Личные данные">
      <form className="flex flex-col gap-4 sm:grid sm:grid-cols-[minmax(0,600px)_minmax(0,600px)] sm:gap-8">
        <Input value={userProfile?.lastName ?? ''} disabled label="Фамилия" />
        <Input value={userProfile?.firstName ?? ''} disabled label="Имя" />
        <Input value={userProfile?.middleName ?? ''} disabled label="Отчество" />
        <Input value={userProfile.inn ?? ''} disabled onChange={() => null} label="ИНН" />
      </form>
      <div className="my-6 flex items-center gap-6">
        <ServiceCitiesIcon />
        <p className="text-[22px] font-bold">Города обслуживания</p>
      </div>
      <div className="flex flex-col gap-4">
        {hasPrimaryCity === true && (
          <AlertBannerBlock
            className="!bg-[#E3F2FE]"
            title={
              <div className="mb-4 flex items-center gap-2">
                <IconAttention className="h-5 w-5 text-primary-60" />
                <p>Обратите внимание</p>
              </div>
            }
            description={
              <p>
                Мы обновили формат указания городов обслуживания. Пожалуйста, проверьте список и
                укажите актуальные значения.
              </p>
            }
          />
        )}
        <div className="flex flex-col gap-4 sm:grid sm:grid-cols-[minmax(0,600px)_minmax(0,600px)] sm:gap-8">
          <CitiesSelect
            isMulti={false}
            control={control}
            label="Основной город"
            name="primarySettlement"
            onChange={onChangeHandler}
            isFormDisabled={false}
            isClearable
            errorMessage={errors?.primarySettlement?.message}
            clearErrors={clearErrors}
          />
          <CitiesSelect
            isMulti={true}
            control={control}
            label="Дополнительные города"
            name="additionalSettlements"
            onChange={onChangeHandler}
            isFormDisabled={false}
            isClearable
          />
        </div>
      </div>
    </PageWrapper>
  );
};
